import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { TextTemplateDto } from '@travel-planner/library-travel-planner-api';
import { LoadingState } from '../../../../core/models/common';
import { TextTemplatesActions } from './text-templates.actions';

export interface TextTemplatesState extends EntityState<TextTemplateDto> {
    loadingState: LoadingState;
    scrollPosition: number;
    totalTextTemplates: number | null;
}

export const textTemplatesAdapter: EntityAdapter<TextTemplateDto> = createEntityAdapter<TextTemplateDto>();

const initialState: TextTemplatesState = textTemplatesAdapter.getInitialState({
    loadingState: 'Initial',
    scrollPosition: 0,
    totalTextTemplates: null,
});

export const textTemplatesFeature = createFeature({
    name: 'textTemplates',
    reducer: createReducer(
        initialState,
        on(TextTemplatesActions.loadTextTemplates, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(TextTemplatesActions.loadTextTemplatesSuccess, (state, { textTemplates, total }) => {
            if (total != null) {
                return {
                    ...textTemplatesAdapter.setAll(textTemplates, state),
                    totalTextTemplates: total,
                    loadingState: 'Loaded' as const,
                    scrollPosition: 0
                };
            }
            return {
                ...textTemplatesAdapter.addMany(textTemplates, state),
                loadingState: 'Loaded' as const
            };
        }),
        on(TextTemplatesActions.scrollTextTemplatesList, (state, { scrollPosition }) => ({
            ...state,
            scrollPosition
        })),
        on(TextTemplatesActions.loadTextTemplateSuccess, (state, { textTemplate }) => {
            return textTemplatesAdapter.setOne(textTemplate, state);
        }),
        on(TextTemplatesActions.updateTextTemplate, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(
            TextTemplatesActions.updateTextTemplateSuccess,
            TextTemplatesActions.uploadTextTemplateImagesSuccess,
            TextTemplatesActions.reorderTextTemplateImagesSuccess,
            (state, { updatedTextTemplate }) => {
                return {
                    ...textTemplatesAdapter.setOne(updatedTextTemplate, state),
                    loadingState: 'Loaded' as const,
                };
            }
        ),
        on(TextTemplatesActions.deleteTextTemplateImageSuccess, (state, { textTemplateId, imageId }) => {
            const tt = state.entities[textTemplateId];
            if (!tt) return state;
            const updatedTextTemplate = {
                ...tt,
                images: (tt.images || []).filter(img => img.id !== imageId)
            };
            return {
                ...textTemplatesAdapter.setOne(updatedTextTemplate, state),
                loadingState: 'Loaded' as const,
            };
        }),
        on(TextTemplatesActions.addTextTemplate, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(TextTemplatesActions.addTextTemplateSuccess, (state, { textTemplate }) => {
            return {
                ...textTemplatesAdapter.setOne(textTemplate, state),
                loadingState: 'Loaded' as const,
            };
        }),
        on(TextTemplatesActions.deleteTextTemplate, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(TextTemplatesActions.deleteTextTemplateSuccess, (state, { id }) => {
            return {
                ...textTemplatesAdapter.removeOne(id, state),
                loadingState: 'Loaded' as const,
            };
        }),
        on(
            TextTemplatesActions.loadTextTemplatesFailure,
            TextTemplatesActions.loadTextTemplateFailure,
            TextTemplatesActions.addTextTemplateFailure,
            TextTemplatesActions.updateTextTemplateFailure,
            TextTemplatesActions.deleteTextTemplateFailure,
            TextTemplatesActions.uploadTextTemplateImagesFailure,
            TextTemplatesActions.deleteTextTemplateImageFailure,
            TextTemplatesActions.reorderTextTemplateImagesFailure,
            (state) => ({
                ...state,
                loadingState: 'Error' as const,
            })
        ),
    ),
});

const { selectTextTemplatesState } = textTemplatesFeature;

const selectSafeTextTemplatesState = createSelector(
    selectTextTemplatesState,
    (state) => state ?? initialState
);

const { selectEntities, selectAll, selectTotal } =
    textTemplatesAdapter.getSelectors(selectSafeTextTemplatesState);

export const selectTextTemplatesLoadingState = createSelector(
    selectSafeTextTemplatesState,
    (state) => state.loadingState
);

export const selectTextTemplatesListScrollPosition = createSelector(
    selectSafeTextTemplatesState,
    (state) => state.scrollPosition
);

export const selectTextTemplates = selectAll;

export const selectTextTemplatesEntities = selectEntities;

export const selectTextTemplatesLoadedCount = selectTotal;

export const selectTextTemplatesTotal = createSelector(
    selectSafeTextTemplatesState,
    (state) => state.totalTextTemplates
);

export const selectTextTemplate = (id: string | number) =>
    createSelector(selectSafeTextTemplatesState, (state) => state.entities[id] || null);
